import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { TranslatePipe } from 'app/core/i18n/translate.pipe';

import { DocumentGenerateService } from '../../document-generate.service';
import { DocumentGenerateConfigService } from '../../document-generate-config.service';
import { DgNumberPipe } from '../../dg-number.pipe';
import { ChangeUnitDialogData, UnitRow } from '../../interfaces/dialog-data.interface';
import { PricedMaterial } from '../../interfaces/document-item.interface';

@Component({
  selector: 'app-change-unit-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    DgNumberPipe,
    TranslatePipe,
  ],
  templateUrl: './change-unit-modal.component.html',
})
export class ChangeUnitModalComponent implements OnInit {
  units: UnitRow[] = [];
  selectedId: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<ChangeUnitModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeUnitDialogData,
    private service: DocumentGenerateService,
    public dgConfig: DocumentGenerateConfigService
  ) {}

  ngOnInit(): void {
    const material = this.data.item.material ?? {};
    const aums: any[] = material.alternalUnitOfMeasures ?? [];
    const stocks: any[] = material.materialStock ?? [];
    const baseName = material.baseUnitOfMeasure?.name ?? material.baseUnitOfMeasure?.code ?? '';
    const warehouseId = this.data.warehouseId;
    // el stock se guarda en la unidad base (un registro por almacén); se expresa por unidad dividiendo por su factor
    const baseAvailable = stocks
      .filter((s) => !warehouseId || s.warehouseId?.toString() === warehouseId)
      .reduce((sum, s) => sum + (s.availableStock || 0), 0);

    this.units = aums
      .filter((a) => a?.unitOfMeasure)
      // UMP oculta cuando la compañía no la tiene habilitada
      .filter((a) => this.data.umpActive !== false || a.isUMP !== true)
      .map((a) => {
        const factor = Number(a.conversionFactor ?? a.equivalentQuantity ?? 1);
        return {
          unitMeasureId: a.unitOfMeasure._id?.toString(),
          code: a.unitOfMeasure.code ?? a.unitOfMeasure.name,
          name: a.unitOfMeasure.name,
          factor,
          chip: a.isUMB ? 'BASE' : a.isSecondaryAlternativeUom ? 'SECUNDARIA' : a.isUMP ? 'PARALELA' : null,
          isUMP: a.isUMP === true,
          isUMB: a.isUMB === true,
          equivalence: `1 ${a.unitOfMeasure.name} = ${factor} ${baseName}`,
          stock: factor > 0 ? baseAvailable / factor : baseAvailable, // stock disponible en esta unidad
          price: 0,
          hasPrice: false,
          isCommercialUom: a.isCommercialUom !== false,
        } as UnitRow;
      });

    // orden: Base, Secundaria, Paralela y luego el resto
    this.units.sort((a, b) => this.chipRank(a.chip) - this.chipRank(b.chip));

    this.selectedId = this.data.item.unitMeasureId;
    // no preseleccionar una unidad que no sea vendible (ej. base no vendible al agregar)
    if (!this.units.some((u) => u.unitMeasureId === this.selectedId && u.isCommercialUom)) {
      this.selectedId = null;
    }
    this.priceUnits();
  }

  private chipRank(chip: UnitRow['chip']): number {
    switch (chip) {
      case 'BASE':
        return 0;
      case 'SECUNDARIA':
        return 1;
      case 'PARALELA':
        return 2;
      default:
        return 3;
    }
  }

  private priceUnits(): void {
    const { businessPartnerId, salesOfficeId, currencyId } = this.data;
    if (!businessPartnerId || !salesOfficeId || !currencyId || !this.units.length) return;

    const materialId = this.data.item._id;
    const payload = {
      businessPartnerId,
      salesOfficeId,
      currencyId,
      materials: this.units.map((u) => ({ materialId, unitMeasureId: u.unitMeasureId, quantity: 1 })),
    };

    this.service.fetchPrice(payload).subscribe({
      next: (res) => {
        const priced: PricedMaterial[] = res?.materials ?? [];
        for (const u of this.units) {
          const found = priced.find(
            (p) => p.materialId?.toString() === materialId && p.unitMeasureId?.toString() === u.unitMeasureId
          );
          u.price = found ? Number(found.price) || 0 : 0;
          u.hasPrice = u.price > 0;
        }
        this.applyRecalc();
      },
      error: () => {},
    });
  }

  // aplica el recálculo de precio para unidades sin precio (modo 1 = deriva desde la UMB)
  private applyRecalc(): void {
    if (this.recalcMode !== 1) return;
    const basePrice = this.units.find((u) => u.isUMB)?.price ?? 0;
    if (basePrice <= 0) return;
    for (const u of this.units) {
      if (!u.hasPrice) u.price = basePrice * u.factor; // precio derivado
    }
  }

  get recalcMode(): number {
    return this.data.recalcMode ?? 2;
  }

  // la UMB tiene precio propio (pivote para derivar en modo 1)
  private get umbHasPrice(): boolean {
    return this.units.find((u) => u.isUMB)?.hasPrice === true;
  }

  // la UMB es vendible (se puede convertir a ella en modo 2)
  private get umbSellable(): boolean {
    const umb = this.units.find((u) => u.isUMB);
    return !umb || umb.isCommercialUom;
  }

  // fila seleccionable: unidad con precio propio siempre; sin precio depende del modo
  // (0=nunca, 1=si la UMB tiene precio para derivar, 2=si la UMB es vendible para convertir)
  selectable(u: UnitRow): boolean {
    if (!u.isCommercialUom) return false;
    if (u.hasPrice) return true;
    if (this.recalcMode === 1) return this.umbHasPrice;
    if (this.recalcMode === 2) return this.umbSellable;
    return false; // modo 0
  }

  select(u: UnitRow): void {
    if (!this.selectable(u)) return;
    this.selectedId = u.unitMeasureId;
  }

  isSelected(u: UnitRow): boolean {
    return this.selectedId === u.unitMeasureId;
  }

  confirm(): void {
    this.dialogRef.close(this.units.find((u) => u.unitMeasureId === this.selectedId));
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
